import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getCases, createCase, deleteCase, getCustomLists, checkDuplicate } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/helpers';
import { getRoleLabel, getRoleBadgeStyle, ATTORNEY_ROLES } from '../utils/roles';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import { Plus, Search, Briefcase, Trash2, AlertTriangle, Pencil, SlidersHorizontal, X } from 'lucide-react';

const STATUS_COLORS = {
  intake:  'var(--status-blue)',
  active:  'var(--status-green)',
  pending: 'var(--status-yellow)',
  closed:  'var(--text-muted)',
  settled: 'var(--zeus-gold)',
};

export default function CasesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();


  const [loading, setLoading]         = useState(true);
  const [cases, setCases]             = useState([]);
  const [showNewCase, setShowNewCase] = useState(false);
  const [newName, setNewName]         = useState('');
  const [newType, setNewType]         = useState('');
  const [newDob, setNewDob]           = useState('');
  const [typeOptions, setTypeOptions] = useState(['WC','Indemnity','Medical Only','Third Party','Death','DOL']);
  const [creating, setCreating]       = useState(false);
  const [dupResults, setDupResults]   = useState([]);
  const [dupChecking, setDupChecking] = useState(false);
  const dupTimer = useRef(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting]         = useState(false);

  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter]   = useState('');
  const [solFilter, setSolFilter]     = useState('');
  const [solRules, setSolRules]       = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PER_PAGE = 25;
  const sentinelRef = useRef(null);

  // Active filter count (for mobile badge)
  const activeFilterCount = [statusFilter, typeFilter, solFilter].filter(Boolean).length;

  // Duplicate check
  useEffect(() => {
    if (dupTimer.current) clearTimeout(dupTimer.current);
    const name = newName.trim();
    if (!name || name.length < 2 || !newDob) { setDupResults([]); return; }
    dupTimer.current = setTimeout(async () => {
      try {
        setDupChecking(true);
        const res = await checkDuplicate(name, newDob);
        setDupResults(res?.duplicates || []);
      } catch { setDupResults([]); }
      finally { setDupChecking(false); }
    }, 400);
    return () => { if (dupTimer.current) clearTimeout(dupTimer.current); };
  }, [newName, newDob]);

  const openNewCaseModal = async () => {
    setNewName(''); setNewType(''); setNewDob(''); setDupResults([]);
    setShowNewCase(true);
    try {
      const lists = await getCustomLists();
      if (lists?.legal_type_options?.length) setTypeOptions(lists.legal_type_options);
    } catch {}
  };

  const handleCreateCase = async (forceCreate = false) => {
    if (!newName.trim()) return toast.error('Claimant name is required');
    if (!newType)        return toast.error('Type is required');
    if (!newDob)         return toast.error('Date of Birth is required');
    try {
      setCreating(true);
      const newCase = await createCase({
        client_name: newName.trim(), claim_type: newType,
        date_of_birth: newDob, status: 'intake',
        skip_duplicate_check: forceCreate,
      });
      toast.success('Case created');
      setShowNewCase(false);
      navigate(`/cases/${newCase._id || newCase.id}`);
    } catch (err) {
      if (err.isDuplicate) {
        setDupResults(err.duplicates || []);
        toast.error(err.message || 'Duplicate case found');
      } else {
        toast.error(err?.response?.data?.error || err?.message || 'Failed to create case');
      }
    } finally { setCreating(false); }
  };

  const openDeleteModal = (e, caseItem) => {
    e.stopPropagation();
    setDeleteTarget(caseItem);
    setDeleteReason('');
  };

  // Check both access_level and role — handles legacy tokens and owner accounts
  const isSuperAdmin = user?.access_level === 'super_admin'
    || user?.role === 'super_admin'
    || user?.role === 'owner';
  const isAdmin = isSuperAdmin
    || ['admin'].includes(user?.access_level || '')
    || ['admin'].includes(user?.role || '');

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const expectedName = (deleteTarget.client_name || deleteTarget.case_number || '').trim();
    if (isSuperAdmin && deleteConfirm.trim().toLowerCase() !== expectedName.toLowerCase()) {
      toast.error('Name confirmation does not match — type the exact client name to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const res = await deleteCase(
        deleteTarget._id || deleteTarget.id,
        deleteReason,
        isSuperAdmin ? deleteConfirm.trim() : undefined
      );
      if (res.deleted)  toast.success('Case permanently deleted');
      if (res.pending)  toast.success('Deletion request sent to admin for approval');
      setDeleteTarget(null);
      setDeleteConfirm('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const load = async (pg = 1, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = { page: pg, per_page: PER_PAGE };
      if (statusFilter) params.status     = statusFilter;
      if (typeFilter)   params.claim_type = typeFilter;
      if (search.trim()) params.search    = search.trim();
      const res = await getCases(params);
      const data = Array.isArray(res) ? res : (Array.isArray(res?.cases) ? res.cases : []);
      if (append) {
        setCases(prev => [...prev, ...data]);
      } else {
        setCases(data);
      }
      setTotal(res?.total        || data.length);
      setTotalPages(res?.total_pages || 1);
      setPage(pg);
    } catch (err) {
      toast.error(err?.message || 'Failed to load cases');
      if (!append) setCases([]);
    } finally { setLoading(false); setLoadingMore(false); }
  };

  // Reset to page 1 when filters change
  useEffect(() => { setCases([]); setPage(1); load(1); }, [statusFilter, typeFilter]);

  // Debounced server search
  useEffect(() => {
    const t = setTimeout(() => { setCases([]); setPage(1); load(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Infinite scroll — IntersectionObserver on sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !loadingMore && page < totalPages) {
        load(page + 1, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page, totalPages, loading, loadingMore, statusFilter, typeFilter, search]);

  useEffect(() => {
    (async () => {
      try {
        const lists = await getCustomLists();
        if (lists?.sol_rules) setSolRules(lists.sol_rules);
      } catch {}
    })();
  }, []);

  const getSOLDate = (c) => {
    if (c.sol_date) return new Date(c.sol_date);
    if (!c.date_of_incident) return null;
    const doi = new Date(c.date_of_incident);
    if (isNaN(doi.getTime())) return null;
    const rule = solRules[c.claim_type || ''];
    const years = rule?.years || 2;
    const sol = new Date(doi);
    sol.setFullYear(sol.getFullYear() + years);
    return sol;
  };

  const isCaseClosed = (c) => (c.status || '').toLowerCase() === 'closed';

  const getSOLDaysLeft = (c) => {
    if (isCaseClosed(c)) return null;
    const sol = getSOLDate(c);
    if (!sol) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    return Math.ceil((sol.getTime() - today.getTime()) / (1000*60*60*24));
  };

  const getSOLTier = (daysLeft, c) => {
    if (c && isCaseClosed(c)) return 'closed';
    if (daysLeft == null) return 'none';
    if (daysLeft < 0)     return 'past_due';
    if (daysLeft <= 30)   return 'critical';
    if (daysLeft <= 90)   return 'urgent';
    if (daysLeft <= 180)  return 'watch';
    return 'safe';
  };

  const getSOLBadge = (c) => {
    if (isCaseClosed(c)) {
      return { color: '#64748b', bg: 'rgba(100,116,139,0.12)', label: 'CLOSED', dayText: '' };
    }
    const sol = getSOLDate(c);
    if (!sol) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const daysLeft = Math.ceil((sol.getTime() - today.getTime()) / (1000*60*60*24));
    let color, bg, label;
    if (daysLeft < 0)       { color='#7c3aed'; bg='rgba(124,58,237,0.12)'; label='PAST DUE'; }
    else if (daysLeft<=30)  { color='#ef4444'; bg='rgba(239,68,68,0.12)';  label='CRITICAL'; }
    else if (daysLeft<=90)  { color='#f97316'; bg='rgba(249,115,22,0.12)'; label='URGENT'; }
    else if (daysLeft<=180) { color='#eab308'; bg='rgba(234,179,8,0.12)';  label='WATCH'; }
    else                    { color='#22c55e'; bg='rgba(34,197,94,0.12)';  label='SAFE'; }
    const dayText = daysLeft < 0 ? `${Math.abs(daysLeft)}d past` : `${daysLeft}d left`;
    return { color, bg, label, dayText };
  };

  const filtered = useMemo(() => {
    // search is now server-side; only solFilter is client-side
    let list = solFilter
      ? cases.filter(c => getSOLTier(getSOLDaysLeft(c), c) === solFilter)
      : cases;
    return [...list].sort((a, b) => {
      const aClosed = isCaseClosed(a), bClosed = isCaseClosed(b);
      // Closed cases always go to the end
      if (aClosed && !bClosed) return 1;
      if (!aClosed && bClosed) return -1;
      if (aClosed && bClosed) return 0;
      const da = getSOLDaysLeft(a), db = getSOLDaysLeft(b);
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  }, [cases, solFilter, solRules]);

  const stats = useMemo(() => ({
    total:   total,
    active:  cases.filter(c => c.status === 'active').length,
    intake:  cases.filter(c => c.status === 'intake').length,
    pending: cases.filter(c => c.status === 'pending').length,
  }), [cases]);

  const clearFilters = () => { setStatusFilter(''); setTypeFilter(''); setSolFilter(''); setSearch(''); setPage(1); };

  return (
    <>
      <Header title="Cases">
        <button className="btn btn-primary btn-sm" onClick={openNewCaseModal} disabled={creating}>
          <Plus size={14} /><span>{creating ? 'Creating...' : 'New Case'}</span>
        </button>
      </Header>

      <div className="app-content">

        {/* ── Stats ── */}
        <div className="cases-stats">
          {[
            { label: 'Total Cases', value: stats.total,   color: 'var(--text-primary)' },
            { label: 'Active',      value: stats.active,  color: 'var(--status-green)' },
            { label: 'Intake',      value: stats.intake,  color: 'var(--status-blue)' },
            { label: 'Pending',     value: stats.pending, color: 'var(--status-yellow)' },
          ].map(s => (
            <div key={s.label} className="stat-card cases-stat">
              <span className="stat-label">{s.label}</span>
              <span className="stat-value" style={{ color: s.color, fontSize: '1.3rem' }}>{s.value}</span>
            </div>
          ))}
        </div>

        {/* ── Filter bar ── */}
        <div className="cases-toolbar">
          {/* Search — always visible */}
          <div className="cases-search-wrap">
            <Search size={15} className="cases-search-icon" />
            <input
              className="cases-search-input"
              placeholder="Search case #, claim #, claimant, attorney…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="cases-search-clear" onClick={() => setSearch('')}><X size={13} /></button>
            )}
          </div>

          {/* Filter selects — visible on desktop, hidden on mobile behind toggle */}
          <div className={`cases-filters${filtersOpen ? ' cases-filters-open' : ''}`}>
            <select className="form-select cases-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="intake">Intake</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="closed">Closed</option>
              <option value="settled">Settled</option>
            </select>
            <select className="form-select cases-filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              <option value="WC">WC</option>
              <option value="Indemnity">Indemnity</option>
              <option value="Medical Only">Medical Only</option>
              <option value="Third Party">Third Party</option>
              <option value="Death">Death</option>
              <option value="DOL">DOL</option>
            </select>
            <select className="form-select cases-filter-select" value={solFilter} onChange={(e) => setSolFilter(e.target.value)}>
              <option value="">All SOL</option>
              <option value="past_due"> Past Due</option>
              <option value="critical"> Critical (≤30d)</option>
              <option value="urgent"> Urgent (≤90d)</option>
              <option value="watch"> Watch (≤180d)</option>
              <option value="safe"> Safe (180d+)</option>
              <option value="closed"> Closed</option>
              <option value="none">-- No SOL --</option>
            </select>
            {activeFilterCount > 0 && (
              <button className="btn btn-ghost btn-sm cases-clear-btn" onClick={clearFilters}>
                <X size={13} /> Clear
              </button>
            )}
          </div>

          {/* Mobile filter toggle + count */}
          <div className="cases-toolbar-right">
            <button
              className={`btn btn-secondary btn-sm cases-filter-toggle${activeFilterCount > 0 ? ' has-filters' : ''}`}
              onClick={() => setFiltersOpen(p => !p)}
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="cases-filter-badge">{activeFilterCount}</span>
              )}
            </button>
            <span className="cases-count">
              {total.toLocaleString()} case{total !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
            <Briefcase size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
            <h3 style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '6px' }}>
              {cases.length === 0 ? 'No cases yet' : 'No matching cases'}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: '16px' }}>
              {cases.length === 0 ? 'Create your first case to get started.' : 'Try adjusting your search or filters.'}
            </p>
            {cases.length === 0 && (
              <button className="btn btn-primary" onClick={openNewCaseModal}>
                <Plus size={14} /> New Case
              </button>
            )}
          </div>
        ) : (
          <>
            {/* ── Desktop Table ── */}
            <div className="card cases-table-view">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Case #</th><th>Claimant</th><th>Case Type</th><th>Claim #</th><th>DWC #</th>
                      <th>Status</th><th>Attorney</th><th>DOI</th><th>SOL Status</th>
                      <th>Demand</th><th style={{ width:'40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => {
                      const solBadge = getSOLBadge(c);
                      return (
                        <tr
                          key={c._id || c.id}
                          onClick={() => navigate(`/cases/${c._id || c.id}`)}
                          style={{ cursor:'pointer' }}
                          onMouseOver={(e) => e.currentTarget.style.background = 'var(--zeus-gold-dim)'}
                          onMouseOut={(e)  => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ fontWeight:600, color:'var(--zeus-gold)', whiteSpace:'nowrap' }}>
                            {c.case_number || '—'}
                          </td>
                          <td style={{ color:'var(--text-primary)', fontWeight:500 }}>
                            {c.client_name || c.claimant?.name || '—'}
                            {c.is_minor && (
                              <span style={{ marginLeft:'6px', padding:'1px 8px', borderRadius:'10px', fontSize:'0.68rem', fontWeight:700, background:'rgba(249,115,22,0.15)', color:'#f97316', verticalAlign:'middle' }}>Minor</span>
                            )}
                          </td>
                          <td>
                            {c.claim_type ? (
                              <span style={{ padding:'3px 9px', borderRadius:10, fontSize:'0.74rem', fontWeight:700,
                                background: c.claim_type === 'WC' || c.claim_type?.toLowerCase().includes('wc') || c.claim_type?.toLowerCase().includes('worker') ? 'rgba(59,130,246,0.12)' : 'rgba(212,168,83,0.12)',
                                color:      c.claim_type === 'WC' || c.claim_type?.toLowerCase().includes('wc') || c.claim_type?.toLowerCase().includes('worker') ? '#3b82f6' : 'var(--zeus-gold)',
              }}>
                                {c.claim_type}
                              </span>
                            ) : <span style={{ color:'var(--text-muted)', fontSize:'0.82rem' }}>—</span>}
                          </td>
                          <td style={{ fontSize:'0.88rem' }}>{c.claim_number || '—'}</td>
                          <td style={{ fontSize:'0.88rem' }}>{c.dwc_number || '—'}</td>
                          <td>
                            <span style={{ padding:'3px 10px', borderRadius:'12px', fontSize:'0.76rem', fontWeight:600, textTransform:'capitalize', background:`${STATUS_COLORS[c.status]||'var(--text-muted)'}18`, color:STATUS_COLORS[c.status]||'var(--text-muted)' }}>
                              {c.status || '—'}
                            </span>
                          </td>
                          <td style={{ fontSize:'0.88rem' }}>{c.attorney_name || '—'}</td>
                          <td style={{ fontSize:'0.88rem', whiteSpace:'nowrap' }}>
                            {c.date_of_incident ? formatDate(c.date_of_incident) : '—'}
                          </td>
                          <td style={{ whiteSpace:'nowrap' }}>
                            {solBadge ? (
                              <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'3px 10px', borderRadius:'12px', fontSize:'0.73rem', fontWeight:700, background:solBadge.bg, color:solBadge.color }}>
                                <span style={{ width:'8px', height:'8px', borderRadius:'50%', background:solBadge.color, flexShrink:0 }} />
                                {solBadge.label}{solBadge.dayText ? ` ${solBadge.dayText}` : ''}
                              </span>
                            ) : <span style={{ color:'var(--text-muted)', fontSize:'0.78rem' }}>—</span>}
                          </td>
                          <td style={{ fontSize:'0.88rem', whiteSpace:'nowrap' }}>
                            {c.demand_amount ? formatCurrency(c.demand_amount) : '—'}
                          </td>
                          <td>
                            <button onClick={(e) => openDeleteModal(e, c)} style={{ background:'none', border:'none', cursor:'pointer', padding:'4px', color:'#ef4444', display:'flex', alignItems:'center', opacity:0.6, transition:'opacity 0.15s' }}
                              onMouseOver={(e) => e.currentTarget.style.opacity='1'}
                              onMouseOut={(e)  => e.currentTarget.style.opacity='0.6'}
                            ><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Mobile Cards ── */}
            <div className="cases-cards-view">
              {filtered.map(c => {
                const solBadge = getSOLBadge(c);
                const statusColor = STATUS_COLORS[c.status] || 'var(--text-muted)';
                return (
                  <div
                    key={c._id || c.id}
                    className="case-card"
                    onClick={() => navigate(`/cases/${c._id || c.id}`)}
                  >
                    {/* Top row: case # + status + delete */}
                    <div className="case-card-top">
                      <span className="case-card-number">{c.case_number || 'No #'}</span>
                      <span className="case-card-status" style={{ background:`${statusColor}18`, color:statusColor }}>
                        {c.status || '—'}
                      </span>
                      <button
                        className="case-card-delete"
                        onClick={(e) => openDeleteModal(e, c)}
                        title="Delete"
                      ><Trash2 size={14} /></button>
                    </div>

                    {/* Claimant name */}
                    <div className="case-card-name">
                      {c.client_name || c.claimant?.name || '—'}
                      {c.is_minor && <span className="case-minor-badge">Minor</span>}
                    </div>

                    {/* Meta grid */}
                    <div className="case-card-meta">
                      {c.claim_type && (
                        <div className="case-card-meta-item">
                          <span className="case-card-meta-label">Type</span>
                          <span className="case-card-meta-value">{c.claim_type}</span>
                        </div>
                      )}
                      {c.attorney_name && (
                        <div className="case-card-meta-item">
                          <span className="case-card-meta-label">Attorney</span>
                          <span className="case-card-meta-value">{c.attorney_name}</span>
                        </div>
                      )}
                      {c.date_of_incident && (
                        <div className="case-card-meta-item">
                          <span className="case-card-meta-label">DOI</span>
                          <span className="case-card-meta-value">{formatDate(c.date_of_incident)}</span>
                        </div>
                      )}
                      {c.demand_amount > 0 && (
                        <div className="case-card-meta-item">
                          <span className="case-card-meta-label">Demand</span>
                          <span className="case-card-meta-value">{formatCurrency(c.demand_amount)}</span>
                        </div>
                      )}
                    </div>

                    {/* SOL badge — bottom */}
                    {solBadge && (
                      <div className="case-card-sol">
                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'3px 10px', borderRadius:'12px', fontSize:'0.72rem', fontWeight:700, background:solBadge.bg, color:solBadge.color }}>
                          <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:solBadge.color, flexShrink:0 }} />
                          {solBadge.label} · {solBadge.dayText}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Infinite scroll sentinel + status */}
            <div ref={sentinelRef} style={{ padding: '16px', textAlign: 'center' }}>
              {loadingMore && <div className="spinner" style={{ margin: '0 auto' }} />}
              {!loadingMore && page < totalPages && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Scroll for more...</span>
              )}
              {!loadingMore && page >= totalPages && cases.length > 0 && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Showing all {total.toLocaleString()} case{total !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Delete Modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => { setDeleteTarget(null); setDeleteConfirm(''); }} title="Delete Case">
        <div style={{ marginBottom:'16px' }}>
          {/* Case info */}
          <div style={{ padding:'12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-md)', border:'1px solid var(--zeus-border)', marginBottom:'14px' }}>
            <div style={{ fontWeight:600, color:'var(--zeus-gold)', fontSize:'0.9rem' }}>{deleteTarget?.case_number || '—'}</div>
            <div style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{deleteTarget?.client_name || 'Unnamed'} · {deleteTarget?.claim_type || '—'}</div>
          </div>

          {isSuperAdmin ? (
            <>
              <p style={{ fontSize:'0.85rem', color:'var(--status-red)', marginBottom:10, fontWeight:600 }}>
                 This will permanently delete the case and all associated records. This cannot be undone.
              </p>
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>
                Type the client name to confirm: <strong style={{ color:'var(--text-primary)' }}>{deleteTarget?.client_name || deleteTarget?.case_number}</strong>
              </label>
              <input
                className="form-input" autoFocus
                placeholder={deleteTarget?.client_name || deleteTarget?.case_number}
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                style={{ marginBottom:10, borderColor: deleteConfirm && deleteConfirm.toLowerCase() !== (deleteTarget?.client_name || deleteTarget?.case_number || '').toLowerCase() ? 'var(--status-red)' : undefined }}
              />
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Reason (optional)</label>
              <textarea className="form-textarea" style={{ minHeight:'60px' }} placeholder="Reason for deletion..." value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
            </>
          ) : isAdmin ? (
            <>
              <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)', marginBottom:10 }}>
                Submitting a <strong>deletion request</strong> for admin approval.
              </p>
              <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>Reason <span style={{ color:'var(--status-red)' }}>*</span></label>
              <textarea className="form-textarea" style={{ minHeight:'70px' }} placeholder="Enter reason..." value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
            </>
          ) : (
            <p style={{ fontSize:'0.88rem', color:'var(--text-muted)', textAlign:'center', padding:'8px 0' }}>
              Only Super Admins and Admins can delete or request deletion of cases.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); }}>Cancel</button>
          {(isSuperAdmin || isAdmin) && (
            <button
              className="btn btn-sm"
              disabled={
                deleting ||
                (isSuperAdmin && deleteConfirm.trim().toLowerCase() !== (deleteTarget?.client_name || deleteTarget?.case_number || '').toLowerCase()) ||
                (isAdmin && !isSuperAdmin && !deleteReason.trim())
              }
              onClick={handleDelete}
              style={{ background:'var(--status-red)', color:'#fff', border:'none' }}
            >
              <Trash2 size={14} /> {deleting ? 'Processing…' : isSuperAdmin ? 'Delete Permanently' : 'Submit Request'}
            </button>
          )}
        </div>
      </Modal>

      {/* ── New Case Modal ── */}
      <Modal isOpen={showNewCase} onClose={() => setShowNewCase(false)} title="New Case">
        <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
          <div>
            <label className="form-label">Claimant Name <span style={{ color:'var(--status-red)' }}>*</span></label>
            <input className="form-input" placeholder="Enter claimant name…" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !dupResults.length) handleCreateCase(); }} />
          </div>
          <div>
            <label className="form-label">Type <span style={{ color:'var(--status-red)' }}>*</span></label>
            <select className="form-select" value={newType} onChange={(e) => setNewType(e.target.value)}>
              <option value="">Select type…</option>
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Date of Birth <span style={{ color:'var(--status-red)' }}>*</span></label>
            <input className="form-input" type="date" value={newDob} onChange={(e) => setNewDob(e.target.value)} />
          </div>

          {dupResults.length > 0 && (
            <div style={{ padding:'14px', borderRadius:'var(--radius-md)', background:'rgba(249,115,22,0.08)', border:'1.5px solid rgba(249,115,22,0.35)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                <AlertTriangle size={18} style={{ color:'#f97316', flexShrink:0 }} />
                <span style={{ fontWeight:700, fontSize:'0.9rem', color:'#f97316' }}>Potential Duplicate{dupResults.length > 1 ? 's' : ''} Found</span>
              </div>
              <p style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:'10px' }}>
                {dupResults.length === 1
                  ? 'A matching case was found. Review before creating a new one.'
                  : `${dupResults.length} matching cases found. Review before creating a new one.`}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {dupResults.map((d, i) => {
                  const matchColors = { ssn: '#ef4444', dob_name: '#f97316', doi_name: '#8b5cf6' };
                  const matchLabels = { ssn: 'SSN Match', dob_name: 'DOB + Name', doi_name: 'DOI + Name' };
                  return (
                  <div key={d._id || i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', borderRadius:'var(--radius-sm)', background:'rgba(0,0,0,0.2)', border:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px', flexWrap:'wrap' }}>
                        <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--zeus-gold)' }}>{d.case_number || 'No #'}</span>
                        <span className="badge" style={{ fontSize:'0.68rem' }}>{d.claim_type}</span>
                        <span style={{ fontSize:'0.7rem', fontWeight:600, padding:'2px 8px', borderRadius:'10px', background:d.status==='closed'||d.status==='settled'?'rgba(100,116,139,0.15)':'rgba(34,197,94,0.12)', color:d.status==='closed'||d.status==='settled'?'#64748b':'#22c55e' }}>{d.status}</span>
                        {d.match_type && (
                          <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'1px 6px', borderRadius:'8px', background:`${matchColors[d.match_type] || '#64748b'}18`, color:matchColors[d.match_type] || '#64748b' }}>
                            {matchLabels[d.match_type] || d.match_type}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{d.client_name}</span>
                      {d.match_detail && <span style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginLeft:'8px' }}>{d.match_detail}</span>}
                    </div>
                    <button className="btn btn-sm" onClick={() => { setShowNewCase(false); navigate(`/cases/${d._id}`); }}
                      style={{ background:'rgba(212,175,55,0.12)', color:'var(--zeus-gold)', border:'1px solid rgba(212,175,55,0.3)', flexShrink:0, display:'flex', alignItems:'center', gap:'5px', fontSize:'0.78rem', fontWeight:600 }}>
                      <Pencil size={12} /> Open
                    </button>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {dupChecking && (
            <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.78rem', color:'var(--text-muted)' }}>
              <div className="spinner" style={{ width:'12px', height:'12px', borderWidth:'2px' }} /> Checking for duplicates…
            </div>
          )}

          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => setShowNewCase(false)}>Cancel</button>
            {dupResults.length > 0 ? (
              <button className="btn btn-sm" onClick={() => handleCreateCase(true)}
                disabled={creating || !newName.trim() || !newType || !newDob}
                style={{ background:'rgba(249,115,22,0.15)', color:'#f97316', border:'1px solid rgba(249,115,22,0.4)', opacity:(creating||!newName.trim()||!newType||!newDob)?0.5:1, display:'flex', alignItems:'center', gap:'5px' }}>
                <Plus size={13} /> {creating ? 'Creating…' : 'Create Anyway'}
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => handleCreateCase(false)}
                disabled={creating || !newName.trim() || !newType || !newDob}
                style={{ opacity:(creating||!newName.trim()||!newType||!newDob)?0.5:1 }}>
                <Plus size={14} /> {creating ? 'Creating…' : 'Create Case'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
